<script setup>
import ConfirmDialog from "@/components/dialogs/ConfirmDialog.vue"
import QuestionService from '@/services/quality/question.service'
import { VDataTable, VDataTableServer } from "vuetify/labs/VDataTable"

const searchQuery = ref("")
const debouncedSearch = ref("")
const isConfirmDialogVisible = ref(false)
const selectedFormType = ref(1)

// Debounce search 500ms so typing doesn't fire a request per keystroke
let searchTimeout = null

watch(searchQuery, val => {
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    debouncedSearch.value = val
    page.value = 1
  }, 500)
})

definePage({
  meta: {
    name: "app-quality-master-question",
    subject: 'quality.menu',
    action: 'master_question',
  },
})

// Data table options
const itemsPerPage = ref(10)
const page = ref(1)
const sortBy = ref()
const orderBy = ref()

const errors = ref({
  name: [],
  code: [],
  status: [],
})

const onError = error => {
  if (error.data) {
    errors.value = error.data.errors
  }
}

const updateOptions = options => {
  page.value = options.page
  orderBy.value = options.sortBy[0]?.key
  sortBy.value = options.sortBy[0]?.order?.toUpperCase()
}

// truncateText throws on null, so guard before calling it.
const labelText = label => (label ? truncateText(label, 40) : null)

const selectedQuestionGroup = ref()

// Headers
const headers = [
  // { title: "", key: "data-table-expand" },
  {
    title: "Label",
    key: "label",
  },
  {
    title: "Order",
    key: "order",
  },
  {
    title: "Question Type",
    key: "question_type",
  },

  // {
  //   title: "Question Key",
  //   key: "question_key",
  // },
  // {
  //   title: "Question Group",
  //   key: "question_group.name",
  //   sortable: false,
  // },
  {
    title: "Status",
    key: "status",
  },
  {
    title: "Actions",
    key: "actions",
    sortable: false,
  },
]

const subQuestionsHeader = [
  {
    title: "Label",
    key: "label",
  },
  {
    title: "Order",
    key: "order",
  },
  {
    title: "Question Type",
    key: "question_type",
  },

  // {
  //   title: "Question Key",
  //   key: "question_key",
  // },

  // {
  //   title: 'Question Group',
  //   key: 'question_group.name',
  //   sortable: false,
  // },
  {
    title: "Status",
    key: "status",
  },
  {
    title: "Actions",
    key: "actions",
    sortable: false,
  },
]

const selectedFormSection = ref()
const formSections = await getEnumValue('FormSections', true)

const selectedQuestionType = ref()
const questionTypeEnum = await getEnumValue('QuestionTypes', true)
const questionTypes = questionTypeEnum.value.find(item => item.value === 'INCIDENT_QUESTION_TYPES')?.title

const { data: questionsData, execute: fetchQuestions } = useApi(
  createUrl("incident/questions", {
    query: {
      paged: 1,
      showAll: true,
      search: {
        label: debouncedSearch,
        form_section: selectedFormSection,
        question_type: selectedQuestionType,
      },
      itemsPerPage,
      page,
      sort: sortBy,
      order_by: orderBy,
    },
  }),
)

const questions = computed(
  () => questionsData.value?.data ?? questionsData.value,
)

const totalQuestions = computed(
  () => questionsData.value?.meta?.total ?? questionsData.value?.length,
)

const isQuestionDrawerVisible = ref(false)
const questionDrawerData = ref()
const questionDeleteData = ref()
const questionDrawerType = ref("Add")

const showQuestion = (questionData, type) => {
  questionDrawerData.value = questionData
  questionDrawerType.value = type
  isQuestionDrawerVisible.value = true
}

const deleteQuestion = async (confirm, data) => {
  if (data?.id && confirm) {
    await QuestionService.destroy(
      data.id,
      () => {
        fetchQuestions()
        isConfirmDialogVisible.value = false
      },
      onError,
    )
  }
}

const showConfirmDialog = questionData => {
  questionDeleteData.value = questionData
  isConfirmDialogVisible.value = true
}

watch(isConfirmDialogVisible, () => {
  if (!isConfirmDialogVisible.value) {
    questionDeleteData.value = {}
  }
})
</script>

<template>
  <section>
    <VCard
      variant="flat"
      class="border"
    >
      <VCardText class="d-flex flex-wrap align-center py-4 gap-4">
        <!-- 👉 Search -->
        <div class="question-search-field">
          <AppTextField
            v-model="searchQuery"
            placeholder="Search"
            density="compact"
          />
        </div>

        <VSpacer />

        <div class="d-flex align-center flex-wrap gap-4">
          <div style="inline-size: 13rem">
            <AppSelect
              v-model="selectedFormSection"
              :items="formSections"
              :item-title="(item) => item.title"
              :item-value="(item) => item.value"
              placeholder="Filter Form Sections"
              :clearable="true"
            />
          </div>

          <div style="inline-size: 13rem">
            <AppSelect
              v-model="selectedQuestionType"
              :items="questionTypes"
              placeholder="Filter Question Type"
              :clearable="true"
            />
          </div>

          <RouterLink
            v-if="$can('create', 'master.question')"
            :to="{ name: 'app-quality-master-question-entry'}"
            class="font-weight-medium text-link"
          >
            <VBtn prepend-icon="tabler-plus">
              Add New Question
            </VBtn>
          </RouterLink>
        </div>
      </VCardText>

      <VDivider />

      <VDataTableServer
        v-model:items-per-page="itemsPerPage"
        v-model:page="page"
        :items="questions ?? []"
        :items-length="totalQuestions ?? 0"
        :headers="headers"
        class="text-no-wrap"
        @update:options="updateOptions"
      >
        <template #expanded-row="slotProps">
          <tr class="!text-normal">
            <td
              colspan="12"
              class="level-bg-color px-6 py-6 overflow-hidden"
            >
              <VCard
                variant="outlined"
                class="table-container w-100"
              >
                <VDataTable
                  :items="slotProps.item?.sub_questions ?? []"
                  :headers="subQuestionsHeader"
                  :items-per-page="-1"
                  hide-default-footer
                  disable-sort
                  class="custom-data-table text-no-wrap overflow-auto"
                >
                  <template #item.label="{ item }">
                    <VMenu
                      v-if="item.label"
                      open-on-hover
                      :close-on-content-click="false"
                    >
                      <template #activator="{ props }">
                        <span v-bind="props">{{ truncateText(item.label, 25) }}</span>
                      </template>
                      <VCard>
                        <VList>
                          <VListItem class="mx-0">
                            <template #title>
                              <VListItemTitle class="text-h6 text-primary">
                                {{ item.label }}
                              </VListItemTitle>
                            </template>
                          </VListItem>
                        </VList>
                      </VCard>
                    </VMenu>
                    <span
                      v-else
                      class="text-medium-emphasis"
                    >—</span>
                  </template>
                  <template #item.status="{ item }">
                    <VChip
                      :color="item.status ? 'success' : 'error'"
                      size="small"
                    >
                      {{ item.status ? 'Active' : 'Inactive' }}
                    </VChip>
                  </template>
                  <template #item.actions="{ item }">
                    <IconBtn
                      v-if="$can('delete', 'master.question')"
                      @click="showConfirmDialog(item)"
                    >
                      <VIcon icon="tabler-trash" />
                    </IconBtn>

                    <RouterLink
                      v-if="$can('update', 'master.question')"
                      :to="{
                        name: 'app-feedback-question-tab',
                        params: { id: item.id },
                      }"
                      class="font-weight-medium text-link"
                    >
                      <VIcon icon="tabler-edit" />
                    </RouterLink>
                  </template>
                </VDataTable>
              </VCard>
            </td>
          </tr>
        </template>

        <template #item.label="{ item }">
          <VMenu
            v-if="item.label"
            open-on-hover
            :close-on-content-click="false"
          >
            <template #activator="{ props }">
              <span
                v-bind="props"
                class="font-weight-medium"
              >{{ labelText(item.label) }}</span>
            </template>
            <VCard>
              <VList>
                <VListItem class="mx-0">
                  <template #title>
                    <VListItemTitle class="text-h6 text-primary">
                      {{ item.label }}
                    </VListItemTitle>
                  </template>
                </VListItem>
              </VList>
            </VCard>
          </VMenu>
          <span
            v-else
            class="text-medium-emphasis"
          >—</span>
        </template>

        <template #item.order="{ item }">
          <span :class="isNullOrUndefined(item.order) ? 'text-medium-emphasis' : ''">
            {{ isNullOrUndefined(item.order) ? '—' : item.order }}
          </span>
        </template>

        <template #item.question_type="{ item }">
          <span :class="item.question_type ? '' : 'text-medium-emphasis'">
            {{ item.question_type || '—' }}
          </span>
        </template>

        <template #item.status="{ item }">
          <VChip
            :color="item.status ? 'success' : 'error'"
            size="small"
          >
            {{ item.status ? 'Active' : 'Inactive' }}
          </VChip>
        </template>

        <template #item.actions="{ item }">
          <IconBtn
            v-if="$can('delete', 'master.question')"
            @click="showConfirmDialog(item)"
          >
            <VIcon icon="tabler-trash" />
          </IconBtn>

          <RouterLink
            v-if="$can('update', 'master.question')"
            :to="{ name: 'app-quality-master-question-entry', params: { id: item.id } }"
            class="font-weight-medium text-link"
          >
            <VIcon icon="tabler-edit" />
          </RouterLink>
        </template>
      </VDataTableServer>
    </VCard>
    <!--
      <QuestionDrawer
      v-model:isDrawerOpen="isQuestionDrawerVisible"
      :question-group-data="questionDrawerData"
      :type="questionDrawerType ?? 'Add'"
      :errors="errors"
      @update:type="questionDrawerType = $event"
      @update:question-group-data="updateQuestion"
      @save-question-group="saveQuestion"
      /> 
    -->
    <ConfirmDialog
      v-model:isDialogVisible="isConfirmDialogVisible"
      confirmation-question="Are you sure?"
      confirm-msg="Question Deleted."
      confirm-title="Deleted!"
      :data="questionDeleteData"
      @confirm="deleteQuestion"
    />
  </section>
</template>

<style>
/* Was a hardcoded #f7f7f7, which stayed light grey in dark theme. */
.level-bg-color {
  background-color: rgba(var(--v-theme-on-surface), 4%);
}

.non-selectable {
  user-select: none !important;
}

.v-data-table-footer {
  border-block-start: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
  margin-block-start: 0 !important;
  padding-block: 0.5rem;
}

/* Nested sub-question table paginates client-side, so it has no footer to show. */
/* stylelint-disable-next-line no-descending-specificity */
.custom-data-table .v-data-table-footer {
  display: none !important;
}
</style>

<style lang="scss" scoped>
.question-search-field {
  inline-size: 100%;

  @media (min-width: 600px) {
    inline-size: 20rem;
  }
}
</style>
